import { capitalize, groupBy, reduce, sortBy } from 'lodash'
import { flow, set } from 'lodash/fp'
import { formatPrice } from 'shared-constants'
import { SESSION_CLIENT_STATUSES } from 'constants/common'
import { getPriceToAxisLabelFormatter } from 'utils/currency'
import { dateToColumnLabel } from '../chartHelpers'

export const filterRevenuesToChart = (
  revenuesByCurrency,
  periodDateGroup,
  currency,
  convertDateToTimezone
) => {
  const obj = reduce(
    revenuesByCurrency[currency],
    (acc, { date, status, value: revenue }) => {
      // Make it simple sortable by generating `key` from `Session.endAtUtc`,
      // transformed to milliseconds from unix epoch, e.g. '1621256679000'.
      const key = `${new Date(date.isoFormat).valueOf()}`
      const columnLabelFieldSetter = set(
        ['chartData', key, 'columnLabel'],
        dateToColumnLabel(periodDateGroup, date, convertDateToTimezone)
      )
      if (status === SESSION_CLIENT_STATUSES.CANCELLED) {
        // skipping canceled
        return acc
      }
      if (status === SESSION_CLIENT_STATUSES.PENDING) {
        return flow([
          columnLabelFieldSetter,
          set(['pendingTotal'], acc.pendingTotal + revenue),
          set(['chartData', key, 'pending'], revenue),
        ])(acc)
      }
      return flow([
        columnLabelFieldSetter,
        set(['paidTotal'], acc.paidTotal + revenue),
        set(['chartData', key, 'paid'], revenue),
      ])(acc)
    },
    { paidTotal: 0, pendingTotal: 0, chartData: {} }
  )
  // Transform `chartData` to array, sorted by `key` (= time in ms)
  // which is 0th item of `[key, value]` pair of `Object.entries`.
  // Sorting is important for Recharts library input
  // because it will draw a bars in order of given array.
  return {
    ...obj,
    chartData: sortBy(Object.entries(obj.chartData), 0).map(([, item]) => item),
  }
}

export const prepareDataForCharts = ({
  sessionRevenues = [],
  periodDateGroup,
  currencyOverride,
  convertDateToTimezone,
}) => {
  const formattedRevenues = sessionRevenues.flatMap(
    ({ date, unix, chartData }) =>
      chartData.map((data) => ({ ...data, date, unix }))
  )
  const revenuesByCurrency = groupBy(formattedRevenues, 'currencyIsoCode')
  const currenciesList = Object.keys(revenuesByCurrency)

  const currency = currencyOverride ?? currenciesList[0]
  const data =
    currency &&
    filterRevenuesToChart(
      revenuesByCurrency,
      periodDateGroup,
      currency,
      convertDateToTimezone
    )

  const total = (data && Math.max(0, data.paidTotal + data.pendingTotal)) || 0

  return {
    currency,
    currenciesList,
    data,
    total,
    yAxisFormatter: getPriceToAxisLabelFormatter(total, currency),
  }
}

export const renderTooltip = (price, name, currency) => {
  let label = capitalize(name)
  if (name === 'paid') {
    label = 'Completed'
  }
  return [
    formatPrice({
      price,
      currency,
    }),
    label,
  ]
}
